/**
 * Global "database epoch" — a counter bumped every time the offline Dexie
 * connection is REOPENED after an unexpected close.
 *
 * The canonical trigger: a second tab logs out, `resetOfflineStorage` calls
 * `offlineDb.delete()`, and the resulting `versionchange` closes THIS tab's
 * Dexie connection. `SyncEngine.recoverFromUnexpectedClose` reopens it in place
 * (`DexieAdapter.reopen`), but the `liveQuery` observers created by the reactive
 * read layer (`dexie-reactive.ts`, `use-shared-live-query.ts`) stay bound to the
 * now-dead connection and never re-fire — every table reads empty and the app
 * renders blank until a manual reload (Sentry `NEXTJS-FRONTEND-2K`/`-26`).
 *
 * Reactive reads include the epoch in their subscription identity, so a bump
 * forces every live query to tear down and re-subscribe on the fresh
 * connection. Bumping the epoch is the ONE signal that repopulates the UI
 * without a reload.
 *
 * Module-level singleton: there is exactly one offline DB and one SyncEngine
 * (see apps/web/CLAUDE.md), so a single shared counter is the whole surface.
 */

let epoch = 0
const listeners = new Set<() => void>()

/** Current epoch. Stable primitive — safe as a `useSyncExternalStore` snapshot. */
export function getDbEpoch(): number {
  return epoch
}

/**
 * Bump the epoch and notify subscribers. Called after a successful reopen so
 * every reactive read re-subscribes on the fresh connection.
 */
export function bumpDbEpoch(): void {
  epoch += 1
  for (const listener of listeners) listener()
}

/** Subscribe to epoch changes. Returns an unsubscribe handle. */
export function subscribeDbEpoch(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Test helper — reset the counter + drop listeners between runs. */
export function __resetDbEpochForTests(): void {
  epoch = 0
  listeners.clear()
}
